import { useState } from 'react'
import { Plus, Package, Trash2, ExternalLink, Loader2 } from 'lucide-react'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCreatorSeedings, useDeleteSeeding } from '@/hooks/use-creators'
import { SeedingFormDialog } from './seeding-form-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface SeedingTabProps {
  creatorId: string
  creatorCpf?: string | null
  creatorAddress?: {
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
  } | null
}

/**
 * Renders a "Histórico de Envios (Seedings)" UI showing a creator's past seedings and a control to create a new seeding.
 *
 * Displays a table of seedings for the given creator, handling loading and empty states, and exposes a button that opens a dialog for creating a new seeding.
 *
 * @param creatorId - The identifier of the creator (influencer) whose seedings will be listed.
 * @returns The rendered seeding tab element.
 */
const DELETABLE_STATUSES = new Set(['pending', 'processing', 'failed', 'returned'])

export function SeedingTab({ creatorId, creatorCpf, creatorAddress }: SeedingTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { data: seedingsResult, isLoading } = useCreatorSeedings(creatorId)
  const deleteSeeding = useDeleteSeeding()

  const seedings = seedingsResult || []

  const statusMap: Record<
    string,
    { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
  > = {
    pending: { label: 'Pendente', variant: 'outline' },
    processing: { label: 'Processando', variant: 'secondary' },
    shipped: { label: 'Enviado', variant: 'default' },
    in_transit: { label: 'Em Trânsito', variant: 'default' },
    delivered: { label: 'Entregue', variant: 'default' },
    failed: { label: 'Falha', variant: 'destructive' },
    returned: { label: 'Devolvido', variant: 'destructive' },
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Histórico de Envios (Seedings)</h3>
        <ChonkyButton onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Envio
        </ChonkyButton>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Itens / Produtos</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Rastreio</TableHead>
              <TableHead>Custo</TableHead>
              <TableHead className="hidden md:table-cell">Pedido ERP</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Carregando envios...
                </TableCell>
              </TableRow>
            ) : seedings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <Package className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    Nenhum envio programado para este influencer ainda.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              seedings.map((seeding) => {
                const statusInfo = statusMap[seeding.status] || {
                  label: seeding.status,
                  variant: 'outline',
                }

                return (
                  <TableRow key={seeding.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(seeding.createdAt), 'dd MMM, yyyy', { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <ul className="text-sm space-y-1">
                        {seeding.products.map((p, i) => (
                          <li key={i}>
                            <span className="font-medium">{p.quantity}x</span> {p.name}
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {seeding.trackingCode ? (
                        seeding.trackingUrl ? (
                          <a
                            href={seeding.trackingUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {seeding.trackingCode}
                          </a>
                        ) : (
                          <span className="text-sm font-medium">{seeding.trackingCode}</span>
                        )
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}

                      {seeding.carrier && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Via {seeding.carrier}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(seeding.totalCost)}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {seeding.tinyOrderId ? (
                        <a
                          href={`https://erp.olist.com/vendas#edit/${seeding.tinyOrderId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          #{seeding.tinyOrderId}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {DELETABLE_STATUSES.has(seeding.status) ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              disabled={deleteSeeding.isPending}
                              onClick={() =>
                                deleteSeeding.mutate({ creatorId, seedingId: seeding.id })
                              }
                            >
                              {deleteSeeding.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Remover envio</TooltipContent>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      <SeedingFormDialog
        creatorId={creatorId}
        creatorAddress={creatorAddress}
        creatorCpf={creatorCpf}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  )
}
